/**
 * useEventRouter - React Hook for Event Router Integration
 * 
 * Provides reactive event routing and read model management for Command Center.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import { apiFetch } from '../services/api_service';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// =============================================================================
// TYPES
// =============================================================================

/**
 * @typedef {Object} ReadModelKey
 * @type {'SNAPSHOT_CONTEXT' | 'EXPOSURE_SUMMARY' | 'FLYWHEEL_PROGRESS' | 'LIABILITY_HEATGRID' | 'LIABILITY_INDEX' | 'PIPELINE' | 'COVERAGE_SUMMARY' | 'AUDIT_PACK_STATUS' | 'CLAIM_VULNERABILITY'}
 */

/**
 * @typedef {Object} RouterContext
 * @property {string} groupId
 * @property {string} boundaryId
 * @property {string} period
 */

// =============================================================================
// INVALIDATION MATRIX
// =============================================================================

const INVALIDATION_MATRIX = {
  // Impact Events
  "ImpactClassified.v1": ["EXPOSURE_SUMMARY", "LIABILITY_INDEX", "PIPELINE", "VALUE_CHAIN_RISK_SIGNALS", "VALUE_CHAIN_RISK_SUPPLIERS"],
  "ImpactEscalated.v1": ["EXPOSURE_SUMMARY", "LIABILITY_INDEX", "LIABILITY_HEATGRID", "PIPELINE", "VALUE_CHAIN_RISK_SIGNALS"],
  "ImpactResolved.v1": ["EXPOSURE_SUMMARY", "LIABILITY_INDEX", "PIPELINE", "VALUE_CHAIN_RISK_SIGNALS"],
  "ImpactMitigated.v1": ["EXPOSURE_SUMMARY", "LIABILITY_INDEX", "VALUE_CHAIN_RISK_SIGNALS", "VALUE_CHAIN_RISK_SUPPLIERS"],
  
  // Obligation Events
  "ObligationTriggered.v1": ["EXPOSURE_SUMMARY", "LIABILITY_HEATGRID", "PIPELINE", "DD_OBLIGATIONS_LIST", "DD_OBLIGATIONS_STATS"],
  "ObligationStatusChanged.v1": ["EXPOSURE_SUMMARY", "LIABILITY_HEATGRID", "PIPELINE", "LIABILITY_INDEX", "DD_OBLIGATIONS_LIST", "DD_OBLIGATIONS_STATS"],
  "ObligationBreached.v1": ["EXPOSURE_SUMMARY", "LIABILITY_HEATGRID", "LIABILITY_INDEX", "AUDIT_PACK_STATUS", "DD_OBLIGATIONS_LIST", "DD_OBLIGATIONS_STATS"],
  "ObligationAssigned.v1": ["EXPOSURE_SUMMARY", "LIABILITY_HEATGRID", "DD_OBLIGATIONS_LIST"],
  "ObligationValidated.v1": ["EXPOSURE_SUMMARY", "LIABILITY_HEATGRID", "DD_OBLIGATIONS_LIST", "DD_OBLIGATIONS_STATS", "FLYWHEEL_PROGRESS"],
  "ObligationReopened.v1": ["EXPOSURE_SUMMARY", "LIABILITY_HEATGRID", "DD_OBLIGATIONS_LIST", "DD_OBLIGATIONS_STATS"],
  
  // Evidence Events
  "EvidenceUploaded.v1": ["FLYWHEEL_PROGRESS", "DD_OBLIGATIONS_LIST"],
  "EvidenceLinkedToObligation.v1": ["FLYWHEEL_PROGRESS", "LIABILITY_INDEX", "DD_OBLIGATIONS_LIST"],
  "EvidenceValidated.v1": ["FLYWHEEL_PROGRESS", "LIABILITY_INDEX", "COVERAGE_SUMMARY", "AUDIT_PACK_STATUS", "DD_OBLIGATIONS_LIST"],
  "EvidenceRejected.v1": ["EXPOSURE_SUMMARY", "LIABILITY_INDEX", "COVERAGE_SUMMARY", "AUDIT_PACK_STATUS", "DD_OBLIGATIONS_LIST"],
  "EvidenceSuperseded.v1": ["LIABILITY_INDEX", "COVERAGE_SUMMARY", "AUDIT_PACK_STATUS"],
  
  // Coverage Events
  "CoverageComputed.v1": ["COVERAGE_SUMMARY", "FLYWHEEL_PROGRESS"],
  "CoverageRegressed.v1": ["COVERAGE_SUMMARY", "LIABILITY_INDEX", "EXPOSURE_SUMMARY"],
  
  // Claim Events
  "ClaimStrengthAdjusted.v1": ["PIPELINE", "CLAIM_VULNERABILITY", "EXPOSURE_SUMMARY"],
  "ClaimLocked.v1": ["FLYWHEEL_PROGRESS", "CLAIM_VULNERABILITY"],
  
  // Audit Events
  "AuditPackGenerated.v1": ["AUDIT_PACK_STATUS", "FLYWHEEL_PROGRESS", "EXPOSURE_SUMMARY"],
  "AuditPackMarkedStale.v1": ["AUDIT_PACK_STATUS", "EXPOSURE_SUMMARY"],
  "SnapshotLocked.v1": ["AUDIT_PACK_STATUS", "SNAPSHOT_CONTEXT"],
  "SnapshotUnlocked.v1": ["AUDIT_PACK_STATUS", "SNAPSHOT_CONTEXT"],
  
  // Entity Events
  "SupplierCreated.v1": ["EXPOSURE_SUMMARY", "PIPELINE", "VALUE_CHAIN_RISK_SUPPLIERS"],
  "SupplierLinked.v1": ["EXPOSURE_SUMMARY", "FLYWHEEL_PROGRESS", "VALUE_CHAIN_RISK_SUPPLIERS"],
  "SupplierRiskAssessed.v1": ["VALUE_CHAIN_RISK_SUPPLIERS", "VALUE_CHAIN_RISK_SIGNALS", "EXPOSURE_SUMMARY"],
  "SupplierStatusChanged.v1": ["VALUE_CHAIN_RISK_SUPPLIERS", "EXPOSURE_SUMMARY"],
  "LegalEntityCreated.v1": ["EXPOSURE_SUMMARY", "VALUE_CHAIN_RISK_SUPPLIERS"],
  "ReportingBoundaryChanged.v1": ["EXPOSURE_SUMMARY", "COVERAGE_SUMMARY"],
  
  // Value Chain Risk Events
  "RiskSignalReceived.v1": ["VALUE_CHAIN_RISK_SIGNALS", "VALUE_CHAIN_RISK_SUPPLIERS", "EXPOSURE_SUMMARY"],
  "RiskSignalProcessed.v1": ["VALUE_CHAIN_RISK_SIGNALS", "PIPELINE"],
  "RiskSignalDismissed.v1": ["VALUE_CHAIN_RISK_SIGNALS"],
  "TierAssessmentCompleted.v1": ["VALUE_CHAIN_RISK_SUPPLIERS", "FLYWHEEL_PROGRESS"],
  
  // DD Obligations Events
  "DDFrameworkUpdated.v1": ["DD_OBLIGATIONS_LIST", "DD_OBLIGATIONS_STATS", "EXPOSURE_SUMMARY"],
  "DDDeadlineApproaching.v1": ["DD_OBLIGATIONS_LIST", "DD_OBLIGATIONS_STATS", "EXPOSURE_SUMMARY"],
  "DDRemediationStarted.v1": ["DD_OBLIGATIONS_LIST", "DD_OBLIGATIONS_STATS", "LIABILITY_HEATGRID"],
  "DDRemediationCompleted.v1": ["DD_OBLIGATIONS_LIST", "DD_OBLIGATIONS_STATS", "LIABILITY_HEATGRID", "FLYWHEEL_PROGRESS"],

  // Legacy event types (from existing useSpineEvents)
  "obligation_state_change": ["EXPOSURE_SUMMARY", "LIABILITY_HEATGRID", "PIPELINE", "LIABILITY_INDEX", "DD_OBLIGATIONS_LIST", "DD_OBLIGATIONS_STATS"],
  "impact_alert": ["EXPOSURE_SUMMARY", "LIABILITY_INDEX", "PIPELINE", "VALUE_CHAIN_RISK_SIGNALS"],
  "liability_update": ["LIABILITY_INDEX", "EXPOSURE_SUMMARY"],
  "claim_strength_change": ["PIPELINE", "CLAIM_VULNERABILITY", "EXPOSURE_SUMMARY"],
  "audit_pack_stale": ["AUDIT_PACK_STATUS", "EXPOSURE_SUMMARY"],
  "supplier_risk_update": ["VALUE_CHAIN_RISK_SUPPLIERS", "VALUE_CHAIN_RISK_SIGNALS", "EXPOSURE_SUMMARY"],
  "risk_signal_new": ["VALUE_CHAIN_RISK_SIGNALS", "VALUE_CHAIN_RISK_SUPPLIERS"]
};

// =============================================================================
// READ MODEL ENDPOINTS
// =============================================================================

// Read models with no backend projection yet. Guarded in fetchReadModel so
// pages don't 404. The dd-obligations list/stats + value-chain signals/suppliers
// models are served by crossmodule_readmodels.py (mounted in spine/api/__init__.py);
// only timeline + heatmap remain unbuilt.
const UNIMPLEMENTED_READ_MODELS = new Set([
  'CLAIM_VULNERABILITY',
  'DD_OBLIGATIONS_TIMELINE',
  'VALUE_CHAIN_RISK_HEATMAP',
]);

const READ_MODEL_ENDPOINTS = {
  // CSDDD Command Center
  SNAPSHOT_CONTEXT: 'snapshot-context',
  EXPOSURE_SUMMARY: 'exposure-summary',
  FLYWHEEL_PROGRESS: 'flywheel-progress',
  LIABILITY_HEATGRID: 'liability-heatgrid',
  LIABILITY_INDEX: 'liability-index',
  PIPELINE: 'adverse-impact-pipeline',
  COVERAGE_SUMMARY: 'coverage-summary',
  AUDIT_PACK_STATUS: 'audit-pack-status',
  CLAIM_VULNERABILITY: 'claim-vulnerability', // Future endpoint
  
  // DD Obligations Module
  DD_OBLIGATIONS_LIST: 'dd-obligations/list',
  DD_OBLIGATIONS_STATS: 'dd-obligations/stats',
  DD_OBLIGATIONS_TIMELINE: 'dd-obligations/timeline',
  
  // Value Chain Risk Module
  VALUE_CHAIN_RISK_SIGNALS: 'value-chain/signals',
  VALUE_CHAIN_RISK_SUPPLIERS: 'value-chain/suppliers',
  VALUE_CHAIN_RISK_HEATMAP: 'value-chain/heatmap'
};

// =============================================================================
// MAIN HOOK
// =============================================================================

/**
 * Event Router Hook - Central nervous system for CSDDD Command Center
 * 
 * @param {RouterContext} context - Query context (groupId, boundaryId, period)
 * @param {Object} options - Hook options
 */
export function useEventRouter(context, options = {}) {
  const {
    enableToasts = true,
    onEvent = null,
    onModeChange = null,
    autoConnect = true
  } = options;

  // Connection state
  const [mode, setMode] = useState('LIVE');
  const [connectionStatus, setConnectionStatus] = useState('DISCONNECTED');
  const [lastEventReceivedAt, setLastEventReceivedAt] = useState(null);
  const [lastEventId, setLastEventId] = useState(null);
  const [connectedSince, setConnectedSince] = useState(null);

  // Snapshot lock state (Audit Freeze Mode)
  const [snapshotLockState, setSnapshotLockState] = useState({
    isLocked: false,
    snapshotId: null,
    lockedBy: null,
    lockedAt: null,
    reason: null
  });

  // Read model states
  const [readModels, setReadModels] = useState({
    SNAPSHOT_CONTEXT: { data: null, isStale: true, fetchInProgress: false },
    EXPOSURE_SUMMARY: { data: null, isStale: true, fetchInProgress: false },
    FLYWHEEL_PROGRESS: { data: null, isStale: true, fetchInProgress: false },
    LIABILITY_HEATGRID: { data: null, isStale: true, fetchInProgress: false },
    LIABILITY_INDEX: { data: null, isStale: true, fetchInProgress: false },
    PIPELINE: { data: null, isStale: true, fetchInProgress: false },
    COVERAGE_SUMMARY: { data: null, isStale: true, fetchInProgress: false },
    AUDIT_PACK_STATUS: { data: null, isStale: true, fetchInProgress: false },
    CLAIM_VULNERABILITY: { data: null, isStale: true, fetchInProgress: false }
  });

  // Event history
  const [eventHistory, setEventHistory] = useState([]);
  const receivedEventIds = useRef(new Set());

  // Socket ref
  const socketRef = useRef(null);

  // Debounce timers
  const debounceTimers = useRef(new Map());
  const DEBOUNCE_MS = 250;

  // ==========================================================================
  // FETCH SNAPSHOT LOCK STATE
  // ==========================================================================

  const fetchSnapshotLockState = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        groupId: context.groupId,
        boundaryId: context.boundaryId,
        period: context.period
      });

      const response = await fetch(
        `${BACKEND_URL}/api/spine/v1/csddd/readmodels/snapshot-lock-state?${params}`,
        { headers: authHeaders() }
      );

      if (response.ok) {
        const data = await response.json();
        setSnapshotLockState(data);
        return data;
      }
    } catch (error) {
      console.error('[EventRouter] Failed to fetch snapshot lock state:', error);
    }
    return snapshotLockState;
  }, [context.groupId, context.boundaryId, context.period]);

  // ==========================================================================
  // LOCK SNAPSHOT (Audit Freeze)
  // ==========================================================================

  const lockSnapshot = useCallback(async (reason) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/spine/v1/csddd/readmodels/snapshot:lock`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            groupId: context.groupId,
            boundaryId: context.boundaryId,
            period: context.period,
            asOfEventId: lastEventId || 'evt_current',
            reason: reason || 'Audit committee review'
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSnapshotLockState(data);
        toast.success(`Snapshot locked: ${data.snapshotId}`);
        return data;
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to lock snapshot');
      }
    } catch (error) {
      console.error('[EventRouter] Failed to lock snapshot:', error);
      toast.error('Failed to lock snapshot');
    }
    return null;
  }, [context.groupId, context.boundaryId, context.period, lastEventId]);

  // ==========================================================================
  // UNLOCK SNAPSHOT (Requires dual approval)
  // ==========================================================================

  const unlockSnapshot = useCallback(async (reason, approvers) => {
    if (!snapshotLockState.snapshotId) {
      toast.error('No locked snapshot to unlock');
      return null;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/spine/v1/csddd/readmodels/snapshot:unlock`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            snapshotId: snapshotLockState.snapshotId,
            reason: reason || 'Audit complete',
            approvers: approvers || ['LEGAL_COUNSEL', 'COMPLIANCE_OWNER']
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSnapshotLockState(data);
        toast.success('Snapshot unlocked');
        return data;
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to unlock snapshot');
      }
    } catch (error) {
      console.error('[EventRouter] Failed to unlock snapshot:', error);
      toast.error('Failed to unlock snapshot');
    }
    return null;
  }, [snapshotLockState.snapshotId]);

  // ==========================================================================
  // FETCH READ MODEL
  // ==========================================================================

  const fetchReadModel = useCallback(async (modelKey) => {
    const endpoint = READ_MODEL_ENDPOINTS[modelKey];
    if (!endpoint) return null;

    // Read models with no backend projection yet — skip the fetch so pages
    // don't fire 404s. Remove a key here once its spine read model ships in
    // spine/api/readmodels_v1.py.
    if (UNIMPLEMENTED_READ_MODELS.has(modelKey)) return null;

    try {
      setReadModels(prev => ({
        ...prev,
        [modelKey]: { ...prev[modelKey], fetchInProgress: true }
      }));

      const params = new URLSearchParams({
        groupId: context.groupId,
        boundaryId: context.boundaryId,
        period: context.period
      });

      const response = await fetch(
        `${BACKEND_URL}/api/spine/v1/csddd/readmodels/${endpoint}?${params}`,
        { headers: authHeaders() }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch ${modelKey}`);
      }

      const data = await response.json();

      setReadModels(prev => ({
        ...prev,
        [modelKey]: {
          data,
          isStale: false,
          fetchInProgress: false,
          lastFetchedAt: new Date().toISOString()
        }
      }));

      return data;
    } catch (error) {
      console.error(`[EventRouter] Failed to fetch ${modelKey}:`, error);
      setReadModels(prev => ({
        ...prev,
        [modelKey]: { ...prev[modelKey], fetchInProgress: false }
      }));
      return null;
    }
  }, [context.groupId, context.boundaryId, context.period]);

  // ==========================================================================
  // INVALIDATE READ MODEL (Debounced)
  // ==========================================================================

  const invalidateReadModel = useCallback((modelKey) => {
    // Mark as stale immediately
    setReadModels(prev => ({
      ...prev,
      [modelKey]: { ...prev[modelKey], isStale: true }
    }));

    // Clear existing debounce timer
    if (debounceTimers.current.has(modelKey)) {
      clearTimeout(debounceTimers.current.get(modelKey));
    }

    // Set new debounced refetch
    const timer = setTimeout(() => {
      fetchReadModel(modelKey);
      debounceTimers.current.delete(modelKey);
    }, DEBOUNCE_MS);

    debounceTimers.current.set(modelKey, timer);
  }, [fetchReadModel]);

  // ==========================================================================
  // HANDLE INCOMING EVENT
  // ==========================================================================

  const handleIncomingEvent = useCallback((event) => {
    const eventId = event.eventId || event.event_id || `${event.eventType || event.event_type}_${Date.now()}`;
    const eventType = event.eventType || event.event_type;

    // Deduplicate
    if (receivedEventIds.current.has(eventId)) {
      return;
    }

    // Record event
    receivedEventIds.current.add(eventId);
    if (receivedEventIds.current.size > 5000) {
      const oldest = receivedEventIds.current.values().next().value;
      receivedEventIds.current.delete(oldest);
    }

    // Update metadata
    setLastEventReceivedAt(event.timestamp || new Date().toISOString());
    setLastEventId(eventId);

    // Add to history
    const eventRecord = {
      id: eventId,
      type: eventType,
      payload: event.payload || event,
      timestamp: event.timestamp || new Date().toISOString(),
      receivedAt: new Date().toISOString()
    };

    setEventHistory(prev => [eventRecord, ...prev.slice(0, 49)]);

    // Get models to invalidate
    const modelsToInvalidate = INVALIDATION_MATRIX[eventType] || [];

    if (modelsToInvalidate.length > 0) {
      modelsToInvalidate.forEach(model => {
        invalidateReadModel(model);
      });

      // Show toast for significant events
      if (enableToasts && ['ObligationBreached.v1', 'EvidenceRejected.v1', 'AuditPackMarkedStale.v1', 'CoverageRegressed.v1'].includes(eventType)) {
        toast.warning(getEventMessage(eventType, event.payload));
      }
    }

    // Notify callback
    if (onEvent) {
      onEvent(eventRecord);
    }
  }, [invalidateReadModel, enableToasts, onEvent]);

  // ==========================================================================
  // CONNECTION HANDLERS
  // ==========================================================================

  const handleConnect = useCallback(() => {
    setConnectionStatus('CONNECTED');
    setConnectedSince(new Date().toISOString());

    if (mode === 'SNAPSHOT') {
      setMode('LIVE');
      // Refetch all models on reconnect
      Object.keys(readModels).forEach(key => {
        fetchReadModel(key);
      });

      if (onModeChange) {
        onModeChange('LIVE');
      }
    }
  }, [mode, fetchReadModel, onModeChange]);

  const handleDisconnect = useCallback(() => {
    setConnectionStatus('DISCONNECTED');
    setMode('SNAPSHOT');

    // Mark all widgets stale
    setReadModels(prev => {
      const updated = {};
      Object.keys(prev).forEach(key => {
        updated[key] = { ...prev[key], isStale: true };
      });
      return updated;
    });

    if (onModeChange) {
      onModeChange('SNAPSHOT');
    }

    if (enableToasts) {
      toast.info('Switched to Snapshot Mode - Live updates paused');
    }
  }, [enableToasts, onModeChange]);

  // ==========================================================================
  // RESUME LIVE
  // ==========================================================================

  const resumeLive = useCallback(() => {
    if (connectionStatus === 'CONNECTED') {
      setMode('LIVE');
      // Refetch all models
      Object.keys(readModels).forEach(key => {
        fetchReadModel(key);
      });

      if (onModeChange) {
        onModeChange('LIVE');
      }

      toast.success('Resumed Live Mode');
    } else {
      // Try to reconnect
      if (socketRef.current) {
        socketRef.current.connect();
      }
    }
  }, [connectionStatus, fetchReadModel, onModeChange]);

  // ==========================================================================
  // MANUAL REFRESH
  // ==========================================================================

  const refreshAll = useCallback(() => {
    // Use the static endpoint keys, not the state object
    const modelKeys = Object.keys(READ_MODEL_ENDPOINTS).filter(k => k !== 'CLAIM_VULNERABILITY');
    console.log('[EventRouter] Refreshing all models:', modelKeys);
    modelKeys.forEach(key => {
      fetchReadModel(key);
    });
  }, [fetchReadModel]);

  const refreshModel = useCallback((modelKey) => {
    fetchReadModel(modelKey);
  }, [fetchReadModel]);

  // ==========================================================================
  // SOCKET CONNECTION EFFECT
  // ==========================================================================

  useEffect(() => {
    if (!autoConnect) return;

    const socket = io(BACKEND_URL, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[EventRouter] Connected to WebSocket');
      handleConnect();
      socket.emit('join_spine_room', { room_type: 'command-center' });
    });

    socket.on('disconnect', () => {
      console.log('[EventRouter] Disconnected from WebSocket');
      handleDisconnect();
    });

    socket.on('spine_room_joined', (data) => {
      console.log('[EventRouter] Joined room:', data.room_type);
    });

    socket.on('spine_event', (data) => {
      handleIncomingEvent(data);
    });

    socket.on('connect_error', (error) => {
      console.error('[EventRouter] Connection error:', error);
      setConnectionStatus('DISCONNECTED');
    });

    socket.on('reconnecting', () => {
      setConnectionStatus('RECONNECTING');
    });

    return () => {
      if (socket) {
        socket.emit('leave_spine_room', { room_type: 'command-center' });
        socket.disconnect();
      }
    };
  }, [autoConnect, handleConnect, handleDisconnect, handleIncomingEvent]);

  // ==========================================================================
  // INITIAL FETCH EFFECT
  // ==========================================================================

  useEffect(() => {
    // Initial fetch of all read models
    Object.keys(READ_MODEL_ENDPOINTS).forEach(key => {
      if (key !== 'CLAIM_VULNERABILITY') { // Skip unimplemented endpoint
        fetchReadModel(key);
      }
    });
    
    // Also fetch snapshot lock state
    fetchSnapshotLockState();
  }, [context.groupId, context.boundaryId, context.period]);

  // ==========================================================================
  // COMPUTED VALUES
  // ==========================================================================

  const dataFreshnessSeconds = useMemo(() => {
    if (!lastEventReceivedAt) return 999;
    const diff = Date.now() - new Date(lastEventReceivedAt).getTime();
    return Math.floor(diff / 1000);
  }, [lastEventReceivedAt]);

  const staleWidgets = useMemo(() => {
    return Object.keys(readModels).filter(key => readModels[key].isStale);
  }, [readModels]);

  const isLive = connectionStatus === 'CONNECTED' && mode === 'LIVE';

  // ==========================================================================
  // RETURN
  // ==========================================================================

  return {
    // Connection state
    mode,
    connectionStatus,
    isConnected: connectionStatus === 'CONNECTED',
    isLive,
    lastEventReceivedAt,
    lastEventId,
    connectedSince,
    dataFreshnessSeconds,
    staleWidgets,

    // Read models
    readModels,
    snapshotContext: readModels.SNAPSHOT_CONTEXT.data,
    exposureSummary: readModels.EXPOSURE_SUMMARY.data,
    flywheelProgress: readModels.FLYWHEEL_PROGRESS.data,
    heatgridData: readModels.LIABILITY_HEATGRID.data,
    liabilityIndex: readModels.LIABILITY_INDEX.data,
    pipelineData: readModels.PIPELINE.data,
    coverageSummary: readModels.COVERAGE_SUMMARY.data,
    auditPackStatus: readModels.AUDIT_PACK_STATUS.data,

    // Snapshot Lock State (Audit Freeze Mode)
    snapshotLockState,
    isSnapshotLocked: snapshotLockState.isLocked,

    // Event history
    eventHistory,

    // Actions
    resumeLive,
    refreshAll,
    refreshModel,
    
    // Snapshot Lock Actions
    lockSnapshot,
    unlockSnapshot,
    fetchSnapshotLockState,

    // For external event injection (testing)
    injectEvent: handleIncomingEvent
  };
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getEventMessage(eventType, payload) {
  const messages = {
    'ObligationBreached.v1': 'Obligation breach detected',
    'EvidenceRejected.v1': 'Evidence was rejected',
    'AuditPackMarkedStale.v1': 'Audit pack marked stale',
    'CoverageRegressed.v1': 'Framework coverage regressed',
    'obligation_state_change': `Obligation state changed`,
    'impact_alert': `Impact alert`,
    'liability_update': `Liability score updated`,
    'claim_strength_change': `Claim strength changed`,
    'audit_pack_stale': `Audit pack stale`
  };
  return messages[eventType] || `Event: ${eventType}`;
}

export default useEventRouter;
